import type { Competitor, Competitors } from "@/hooks/firestore/betting/types";
import type { PickemsContestLeaderboardEntry } from "@/hooks/firestore/models/pickems";
import type { ProcessedRacingCompetitor } from "@/hooks/firestore/betting/useRacingEvents";
import type { ProcessedBettingCompetitor } from "@/hooks/firestore/betting/useEvent";

export type EntryType = "BET" | "PICKEM";
export type MarketEventType = "MATCH" | "RACE";
export type OddsFormat =
  | "decimal"
  | "moneyline"
  | "points"
  | "american"
  | "fractional";

// Head to head market competitor outcome types
export const OUTCOME_TYPES = [
  "FRANCHISE_HOME",
  "FRANCHISE_AWAY",
  "PLAYER_HOME",
  "PLAYER_AWAY",
  "DRAW",
  "SRM",
  "CUSTOM",
  "OVER",
  "UNDER",
  "YES",
  "NO",
  "ODD",
  "EVEN",
] as const;

export type OutcomeType = (typeof OUTCOME_TYPES)[number];

export const OUTCOME_RESULTS = [
  "UNDECIDED",
  "WIN",
  "LOSE",
  /**
   * The outcome has been voided. Indicates that the punter's stake should be returned or this outcome should not affect the result of a multi.
   */
  "VOID",
] as const;
export type OutcomeResult = (typeof OUTCOME_RESULTS)[number];

// The top/main market types for each event. If an event does not have one of these markets it will not be displayed
export const TOP_MARKET_TYPES = [
  "WIN_LOSE_DRAW_INC_OT",
  "WINNER_INC_OT",
  "WINNER_INC_EI",
  "WINNER_INC_SUPER_OVER",
  "DRAW_NO_BET_INC_OT",
  "WINNER_INC_OT_PENALTIES",
  //put markets inc OT first
  "WIN_LOSE_DRAW",
  "WINNER",
] as const;
export type TopMarketType = (typeof TOP_MARKET_TYPES)[number];

// these are ordered for display
export const MARKET_TYPES = [
  ...TOP_MARKET_TYPES,
  "DRAW_NO_BET",
  "DOUBLE_CHANCE",
  "MAP_HANDICAP",
  "MAP_HANDICAP_DRAW",
  "CORRECT_MAP_SCORE",
  "MAPN_WINNER_INC_OT",
  "MAPN_ROUNDN_WINNER",
  "MAPN_ROUND_HANDICAP",
  "MAPN_TOTAL_ROUNDS",
  // Lines (+alternate)
  //
  // Totals (+alternate)
  "TOTAL_MAPS",
  "TOTAL",
  "TOTAL_INC_OT",
  "HOME_TOTAL_INC_OT",
  "AWAY_TOTAL_INC_OT",
  // Handicaps (+alternate)
  "HANDICAP",
  "HANDICAP_INC_OT",
  // Doubles markets (HT/FT)
  "HALF_TIME_FULL_TIME",
  // Player Markets
  "PLAYER_TOTAL_POINTS_INC_OT",
  "PLAYER_TOTAL_ASSISTS_INC_OT",
  "PLAYER_TOTAL_REBOUNDS_INC_OT",
  "PLAYER_TOTAL_3POINTS_INC_OT",
  // 1st half markets
  "FIRST_HALF_WIN_LOSE_DRAW",
  "FIRST_HALF_DRAW_NO_BET",
  "FIRST_HALF_TOTAL",
  "FIRST_HALF_HOME_TOTAL",
  "FIRST_HALF_AWAY_TOTAL",
  "FIRST_HALF_HANDICAP",
  "FIRST_HALF_ODD_EVEN",
  // 2nd half markets
  "SECOND_HALF_WIN_LOSE_DRAW",
  "SECOND_HALF_DRAW_NO_BET",
  "SECOND_HALF_TOTAL",
  "SECOND_HALF_HOME_TOTAL",
  "SECOND_HALF_AWAY_TOTAL",
  "SECOND_HALF_HANDICAP",
  "SECOND_HALF_ODD_EVEN",
  // 1st to 4th Quarter markets
  "QUARTERN_ODD_EVEN",
  "QUARTERN_WIN_LOSE_DRAW",
  "QUARTERN_DRAW_NO_BET",
  "QUARTERN_TOTAL",
  "QUARTERN_HANDICAP",
] as const;
export type MarketType = TopMarketType | (typeof MARKET_TYPES)[number];

export const MARKET_STATUSES = [
  "ACTIVE",
  "SUSPENDED",
  "DEACTIVATED",
  "SETTLED",
  "CANCELLED",
  "UNAVAILABLE",
  "LIVE",
  "WAITING",
  "OPEN",
  "HANDED_OVER",
  "INVALID",
] as const;
export type MarketStatus = (typeof MARKET_STATUSES)[number];

export const MATCH_STATUSES = [
  "LIVE",
  "HISTORICAL",
  "PENDING",
  "COMPLETED",
  "CANCELLED",
] as const;
export type MatchStatus =
  | (typeof MATCH_STATUSES)[number]
  | PickemsContestLeaderboardEntry["status"];

export const RESULT_TYPES = ["UNDECIDED", "WIN", "LOSE", "VOID"] as const;
export type ResultType = (typeof RESULT_TYPES)[number];

export const PRODUCT_TYPES = ["BETTING", "PICKEMS"] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

export const CURRENCY_TYPES = ["AUD", "EUR", "USD", "NZD", "CAD"] as const;
export type CurrencyType = (typeof CURRENCY_TYPES)[number];

export type FranchiseType = {
  id: string;
  abbreviation: string;
  name: string;
  sourceId: string;
  sport: string;

  /* Betting props */
  iconUri?: string;
};

export interface IMatchEvent {
  id: string;
  attributes: {
    parentName?: string;
    tournamentName?: string;
    seasonName?: string;
    roundName?: string;
    matchMode?: string;
    streamExpected?: boolean;
  } & any;
  eventId: string;
  eventName: string;
  eventType: MarketEventType;
  scheduledStartTime: Date;
  source: {
    id: string;
    source: string;
  };
  sport: string;
  competitors: Competitors;
  streamUri?: string;
}

export interface IMarket {
  attributes?: {
    custom?: boolean;
    multiable?: boolean;
    promotionCashDisabled?: boolean;
    venueName?: string;
  };
  betStatus?: string;
  bettingMarketId: string;
  createdAt: string;
  disabledProducts?: ProductType;
  event?: IMatchEvent;
  eventReferenceId: string;
  id: string;
  name: string;
  nextBetStop?: Date;
  outcomes: Outcome[];
  scheduledStartTime?: Date;
  specifiers: Record<string, string>;
  status: MarketStatus;
  topMarket?: boolean;
  type: MarketType;
}

export type Outcome = {
  id: string;
  competitorId?: string;
  competitor?: Competitor;
  type: OutcomeType;
  result: ResultType;

  /* Betting props */
  selected?: boolean;
  selectedAgainst?: boolean;
  punted?: boolean;
  abbreviation?: string;
  name?: string;
  odds?: number;
  oddsFormat?: string;
  eventId: string;
  marketId: string;
  active?: boolean;
  deadHeatFactor?: number;
  deductions?: number;
  settlementOdds?: number;
  points?: number;
};

export type SubOutcome = {
  competitor?: Competitor;
  outcomeName: string;
  outcomeType: string;
  outcomeResult: string;
  attributes: {
    runnerNumber: string;
    startingPosition: string;
  };
  marketId: string;
  marketName: string;
  marketType: string;
  marketStatus: MarketStatus;
  index: number;
  type: string;
  statusText: string;
  statusVariant: string;
};

export type SubOutcomesType = {
  name: string;
  outcomeId: string;
  marketId: string;
  marketName?: string;
  marketStatus?: MarketStatus;
  marketType?: string;
  outcomeType?: string;
  attributes?: Record<string, string>;
  outcomeResult?: string;
  competitor?:
    | Competitor
    | ProcessedRacingCompetitor
    | ProcessedBettingCompetitor;
  type?: string;
  odds?: number;
};

export enum BettingTabs {
  SPORTS_SINGLES = "SPORTS_SINGLES",
  SPORTS_SEM = "SPORTS_SEM",
  RACING_SINGLES = "RACING_SINGLES",
  RACING_SEM = "RACING_SEM",
  RACING_EXOTICS = "RACING_EXOTICS",
  RACING_TRIFECTA = "RACING_TRIFECTA",
  RACING_QUINELLA = "RACING_QUINELLA",
  RACING_FIRST_4 = "RACING_FIRST_4",
  RACING_EXACTA = "RACING_EXACTA",
  RACING_QUADDIES = "RACING_QUADDIES",
}

export const ExoticTabs = [
  BettingTabs.RACING_TRIFECTA,
  BettingTabs.RACING_QUINELLA,
  BettingTabs.RACING_FIRST_4,
  BettingTabs.RACING_EXACTA,
];
